import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { Empty, ErrorBox, Loading, Notice, PageHeader, StatusBadge, fmtDate } from '../components/ui';
import { CurriculumEditor } from '../components/CurriculumEditor';

type Tab = 'tutors' | 'incidents' | 'consents' | 'content';

export function AdminPage() {
  const { user } = useAuth();
  const sg = user?.role === 'safeguarding_admin';
  const [tab, setTab] = useState<Tab>(sg ? 'incidents' : 'content');
  const tabs: Array<[Tab, string]> = sg
    ? [['incidents', 'Incident queue'], ['tutors', 'Tutor vetting'], ['consents', 'Consent checks'], ['content', 'Content review']]
    : [['content', 'Content review']];
  return (
    <>
      <PageHeader title={sg ? 'Safeguarding admin' : 'Content review'} lead="Every action here is written to the audit log." />
      <div className="tabs" role="tablist">
        {tabs.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'incidents' && <Incidents />}
      {tab === 'tutors' && <Tutors />}
      {tab === 'consents' && <Consents />}
      {tab === 'content' && <Content />}
    </>
  );
}

function Incidents() {
  const { data, error, loading, reload } = useApi<{ incidents: Array<{ id: string; category: string; severity: string; status: string; description: string; tutor_name: string | null; reporter_role: string | null; auto_action: string | null; created_at: string }> }>('/admin/incidents');
  const act = useAction();
  if (loading) return <Loading />;
  return (
    <div className="stack">
      <ErrorBox error={error ?? act.error} />
      {data?.incidents.length === 0 && <Empty>No incidents. 🌿</Empty>}
      {data?.incidents.map((i) => (
        <div key={i.id} className="card stack">
          <div className="spread">
            <strong>
              {i.category.replace('_', ' ')} <StatusBadge status={i.severity} />
            </strong>
            <StatusBadge status={i.status} />
          </div>
          <p style={{ whiteSpace: 'pre-wrap' }}>{i.description}</p>
          <p className="small muted">
            Reported by {i.reporter_role ?? 'unknown'} · {fmtDate(i.created_at)} {i.tutor_name && `· tutor: ${i.tutor_name}`} {i.auto_action && `· auto: ${i.auto_action}`}
          </p>
          <div className="row">
            {(['triaged', 'escalated', 'resolved'] as const).map((s) => (
              <button key={s} className="btn small secondary" disabled={i.status === s} onClick={async () => { await act.run(() => api(`/admin/incidents/${i.id}`, { method: 'PATCH', body: { status: s } })); reload(); }}>
                Mark {s}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Tutors() {
  const { data, error, loading, reload } = useApi<{
    tutors: Array<{ id: string; display_name: string; email: string; status: string; suspended_reason: string | null; checks: Array<{ check_type: string; status: string }>; vetting: { complete: boolean; missing: string[] } }>;
  }>('/admin/tutors');
  const act = useAction();
  const [check, setCheck] = useState<Record<string, { checkType: string; status: string; notes: string }>>({});
  if (loading) return <Loading />;
  return (
    <div className="stack">
      <ErrorBox error={error ?? act.error} />
      <Notice text={act.notice} />
      {data?.tutors.map((t) => {
        const c = check[t.id] ?? { checkType: 'identity', status: 'passed', notes: '' };
        return (
          <div key={t.id} className="card stack">
            <div className="spread">
              <strong className="yo">{t.display_name}</strong>
              <span className="row">
                <span className="small muted">{t.email}</span>
                <StatusBadge status={t.status} />
              </span>
            </div>
            <p className="small">
              Checks: {t.checks.map((ch) => `${ch.check_type}:${ch.status}`).join(', ') || 'none'}
              {!t.vetting.complete && <> · <strong>missing:</strong> {t.vetting.missing.join(', ')}</>}
            </p>
            <form
              className="row"
              onSubmit={async (e) => {
                e.preventDefault();
                await act.run(() => api(`/admin/tutors/${t.id}/checks`, { body: { ...c, notes: c.notes || undefined } }), 'Check recorded.');
                reload();
              }}
            >
              <select aria-label="Check type" value={c.checkType} onChange={(e) => setCheck({ ...check, [t.id]: { ...c, checkType: e.target.value } })}>
                <option value="identity">Identity</option>
                <option value="reference">Reference</option>
                <option value="background">Background</option>
                <option value="safeguarding_training">Safeguarding training</option>
              </select>
              <select aria-label="Result" value={c.status} onChange={(e) => setCheck({ ...check, [t.id]: { ...c, status: e.target.value } })}>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="not_available">Not available (background only)</option>
              </select>
              <input aria-label="Notes" placeholder="Notes / evidence ref" value={c.notes} onChange={(e) => setCheck({ ...check, [t.id]: { ...c, notes: e.target.value } })} />
              <button className="btn small secondary">Record</button>
            </form>
            <div className="row">
              <button className="btn small" disabled={!t.vetting.complete || t.status === 'approved'} onClick={async () => { await act.run(() => api(`/admin/tutors/${t.id}/approve`, { method: 'POST', body: {} }), 'Approved.'); reload(); }}>
                Approve
              </button>
              <button className="btn small danger" disabled={t.status === 'suspended'} onClick={async () => { const reason = prompt('Reason for suspension?'); if (reason) { await act.run(() => api(`/admin/tutors/${t.id}/suspend`, { body: { reason } }), 'Suspended.'); reload(); } }}>
                Suspend
              </button>
              <button className="btn small ghost" onClick={async () => { await act.run(() => api(`/admin/tutors/${t.id}/reject`, { method: 'POST', body: {} })); reload(); }}>
                Reject
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Consents() {
  const { data, error, loading, reload } = useApi<{ consents: Array<{ id: string; type: string; method: string; child_first_name: string | null; parent_name: string; evidence: Record<string, unknown>; created_at: string }> }>('/admin/consents');
  const act = useAction();
  if (loading) return <Loading />;
  return (
    <div className="stack">
      <ErrorBox error={error ?? act.error} />
      {data?.consents.length === 0 && <Empty>No consents waiting.</Empty>}
      {data?.consents.map((c) => (
        <div key={c.id} className="card spread">
          <div>
            <strong className="yo">{c.parent_name}</strong> for <span className="yo">{c.child_first_name}</span> · {c.method.replace('_', ' ')}
            <div className="small muted">
              ref: {String(c.evidence.reference ?? '—')} · {fmtDate(c.created_at)}
            </div>
          </div>
          <div className="row">
            <button className="btn small" onClick={async () => { await act.run(() => api(`/admin/consents/${c.id}/decide`, { body: { decision: 'verify' } })); reload(); }}>
              Verify
            </button>
            <button className="btn small ghost" onClick={async () => { await act.run(() => api(`/admin/consents/${c.id}/decide`, { body: { decision: 'reject' } })); reload(); }}>
              Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Content() {
  const { user } = useAuth();
  const { data, error, loading, reload } = useApi<{
    lessons: Array<{ id: string; title: string; slug: string; game_type: string; review_status: string; ai_generated: boolean; content: Record<string, unknown> }>;
    terms: Array<{ language_code: string; language_name: string; native_name: string; key: string; term: string; note: string | null }>;
  }>('/admin/content-review');
  const act = useAction();
  const [topic, setTopic] = useState('');
  const editor = user?.role === 'content_editor';
  if (loading) return <Loading />;
  return (
    <div className="stack">
      <ErrorBox error={error ?? act.error} />
      <Notice text={act.notice} />
      <CurriculumEditor />
      <LanguageTerms terms={data?.terms ?? []} editor={editor} onDecided={reload} />
      <LanguageRequests />
      {editor && (
        <form
          className="card row"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await act.run(() => api<{ aiAvailable: boolean; message?: string }>('/lessons/generate', { body: { languageCode: 'yo', topic, ageBand: '7-9', itemCount: 6 } }));
            if (r) act.setNotice(r.aiAvailable ? 'AI draft added to the queue (needs native review).' : r.message ?? 'AI not configured.');
            reload();
          }}
        >
          <label htmlFor="topic">Draft a lesson with AI</label>
          <input id="topic" placeholder="Topic, e.g. fruits" value={topic} onChange={(e) => setTopic(e.target.value)} required />
          <button className="btn small secondary">Generate draft</button>
        </form>
      )}
      <div className="table-wrap card">
        <table className="table">
          <thead>
            <tr>
              <th>Lesson</th>
              <th>Type</th>
              <th>Source</th>
              <th>Status</th>
              {editor && <th>Decision</th>}
            </tr>
          </thead>
          <tbody>
            {data?.lessons.map((l) => (
              <tr key={l.id}>
                <td>
                  <span className="yo">{l.title}</span>
                  <details>
                    <summary className="small">content</summary>
                    <pre className="yo small" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(l.content, null, 1)}</pre>
                  </details>
                </td>
                <td>{l.game_type}</td>
                <td>{l.ai_generated ? <span className="badge warn">AI draft</span> : <span className="badge">human</span>}</td>
                <td>
                  <StatusBadge status={l.review_status} />
                </td>
                {editor && (
                  <td className="row">
                    <button className="btn small" onClick={async () => { await act.run(() => api(`/lessons/${l.id}/review`, { body: { decision: 'approve' } }), 'Approved.'); reload(); }}>
                      Approve
                    </button>
                    <button className="btn small ghost" onClick={async () => { await act.run(() => api(`/lessons/${l.id}/review`, { body: { decision: 'reject' } })); reload(); }}>
                      Reject
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Which languages families are actually asking for, most wanted first. Lessons exist for Yorùbá
 * only; this is the queue that decides what gets written next. One vote per family, no names.
 */
function LanguageRequests() {
  const { data, error, loading } = useApi<{
    requests: Array<{ code: string; name: string; native_name: string; region: string | null; status: string; families: number; last_requested_at: string }>;
  }>('/admin/language-requests');
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const requests = data?.requests ?? [];
  return (
    <div className="card stack">
      <h2 style={{ margin: 0 }}>Lessons requested by families</h2>
      <p className="small muted" style={{ margin: 0 }}>
        Every listed language already works for the Story Vault and the Voice Tree. These are the families asking for
        lessons and games next.
      </p>
      {requests.length === 0 ? (
        <Empty>No requests yet.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Language</th>
                <th>Region</th>
                <th>Families asking</th>
                <th>Latest</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.code}>
                  <td>
                    {r.name} <span className="yo muted">· {r.native_name}</span>
                  </td>
                  <td className="small muted">{r.region ?? '—'}</td>
                  <td>{r.families}</td>
                  <td className="small muted">{fmtDate(r.last_requested_at, false)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/**
 * Interface words waiting on a native speaker.
 *
 * This is the whole distance between a language being listed and a family
 * feeling it. Until these are approved, a family in that language sees the
 * interface in plain English — never in somebody else's language.
 */
function LanguageTerms({ terms, editor, onDecided }: { terms: Array<{ language_code: string; language_name: string; native_name: string; key: string; term: string; note: string | null }>; editor: boolean; onDecided: () => void }) {
  const act = useAction();
  const [edits, setEdits] = useState<Record<string, string>>({});
  if (terms.length === 0) return null;

  const byLanguage = new Map<string, typeof terms>();
  for (const t of terms) {
    const list = byLanguage.get(t.language_code) ?? [];
    list.push(t);
    byLanguage.set(t.language_code, list);
  }

  return (
    <section className="card stack">
      <h2>Interface words awaiting a native speaker</h2>
      <p className="muted">
        These are the greetings and category names a family sees in their own language. Until you approve them, that family gets the interface in English — which is
        honest, but plain. Correcting a word counts as approving your correction.
      </p>
      <ErrorBox error={act.error} />
      <Notice text={act.notice} />
      {[...byLanguage.entries()].map(([code, list]) => (
        <div key={code} className="stack tight">
          <h3 style={{ margin: '10px 0 0' }}>
            {list[0]!.language_name} <span className="muted small">{list[0]!.native_name}</span> · {list.length} words
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Where it appears</th>
                <th>Word</th>
                {editor && <th>Decision</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((t) => {
                const id = `${t.language_code}:${t.key}`;
                return (
                  <tr key={id}>
                    <td>
                      <span className="small">{t.note ?? t.key}</span>
                    </td>
                    <td>
                      {editor ? (
                        <input
                          aria-label={`Word for ${t.key}`}
                          className="yo"
                          lang={t.language_code}
                          value={edits[id] ?? t.term}
                          onChange={(e) => setEdits({ ...edits, [id]: e.target.value })}
                        />
                      ) : (
                        <span className="yo" lang={t.language_code}>
                          {t.term}
                        </span>
                      )}
                    </td>
                    {editor && (
                      <td className="row">
                        <button
                          className="btn small"
                          disabled={act.busy}
                          onClick={async () => {
                            await act.run(
                              () => api('/admin/language-terms/decide', { body: { languageCode: t.language_code, key: t.key, decision: 'approved', term: edits[id] ?? t.term } }),
                              'Approved — families in this language will see it now.',
                            );
                            onDecided();
                          }}
                        >
                          Approve
                        </button>
                        <button
                          className="btn small ghost"
                          disabled={act.busy}
                          onClick={async () => {
                            await act.run(() => api('/admin/language-terms/decide', { body: { languageCode: t.language_code, key: t.key, decision: 'rejected' } }), 'Rejected.');
                            onDecided();
                          }}
                        >
                          Reject
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}
